import { useState } from 'react'
import type { ApolloClient } from '@apollo/client'
import { ADMIN_ENTITY_CARD_ID, ADMIN_USER_CARD_ID } from 'routes/paths'
import type { ModalTypes } from 'components/admin/modals/constants'
import {
  resetPasswordToDefault,
  fullPasswordReset,
  setLastDayAccessToReader
} from 'api/mutations'
import type { EntityReaderPayload, User } from 'api/data.types'
import { post_event } from 'utils/analytics'

const useUsersTable = ({
  client,
  user
}: {
  client: ApolloClient<unknown>
  user: User
}) => {
  const [showUserModal, setShowUserModal] = useState<boolean>(false)
  const [showMoveUserModal, setShowMoveUserModal] = useState<boolean>(false)
  const [userModalType, setUserModalType] = useState<ModalTypes>()
  const [userInfo, setUserInfo] = useState<EntityReaderPayload>(null)
  const [loansActiveOptions, setLoansActiveOptions] = useState([])

  const handleOpenMoveUserModal = (user: EntityReaderPayload) => {
    setUserInfo(user)
    setShowMoveUserModal(true)
  }

  const handleCloseMoveUserModal = () => {
    setShowMoveUserModal(false)
  }

  const handleClickMakeLoan = data => {
    const loan = loansActiveOptions.find(loan => loan.readerId === data.id)

    if (loan) {
      setLoansActiveOptions(
        loansActiveOptions.map(loan =>
          loan.readerId === data.id ? { ...loan, isMakingLoan: true } : loan
        )
      )

      client
        .mutate({
          mutation: setLastDayAccessToReader,
          variables: {
            days: loan.option.id,
            readerId: data.id
          }
        })
        .then(res => {
          setLoansActiveOptions(
            loansActiveOptions.map(loan =>
              loan.readerId === data.id
                ? { ...loan, isMakingLoan: false }
                : loan
            )
          )

          const post_event_value = {
            reader_id: data.id,
            loan_duration: loan.option.id
          }

          post_event(`loan_login`, JSON.stringify(post_event_value))

          setTimeout(() => {
            global.location.reload()
          }, 1000)
        })
        .catch(err => {
          setLoansActiveOptions(
            loansActiveOptions.map(loan =>
              loan.readerId === data.id
                ? { ...loan, isMakingLoan: false }
                : loan
            )
          )

          window.alert(`Oops, aconteceu um erro: ${err.message}`)
        })
    }
  }

  const handleOpenUserModal = (
    type: ModalTypes,
    user?: EntityReaderPayload
  ) => {
    setUserInfo(user || null)
    setUserModalType(type)
    setShowUserModal(true)
  }

  const handleCloseUserModal = () => {
    setUserInfo(null)
    setUserModalType(null)
    setShowUserModal(false)
  }

  const handleClickOnStudentCardButton = entity => {
    window.open(ADMIN_ENTITY_CARD_ID(entity.id), '_blank')
  }

  const handleResetUserPassword = (userToReset: EntityReaderPayload) => {
    const confirmed = global.confirm(
      `Você tem certeza que deseja resetar a senha do usuário "${userToReset.name}"?`
    )

    if (confirmed) {
      const useRandomPassword = user.featureToggles?.doActivityV2
      const mutation = useRandomPassword
        ? fullPasswordReset
        : resetPasswordToDefault

      client
        .mutate({
          mutation,
          variables: {
            readerId: userToReset.id
          }
        })
        .then(response => {
          if (useRandomPassword) {
            const newPassword = response.data?.fullPasswordReset
            global.alert(`A senha foi resetada para: ${newPassword}`)
          } else {
            global.alert('Agora a senha desse usuário é arvore12345')
          }
        })
        .catch(err => window.alert(`Oops, aconteceu um erro: ${err.message}`))
    }
  }

  const handleShowUserCardID = (user: EntityReaderPayload) => {
    window.open(ADMIN_USER_CARD_ID(user.id), '_blank')
  }

  const handleOnChangeLoansOptions = (item, readerId) => {
    setLoansActiveOptions(
      loansActiveOptions.some(loan => loan.readerId === readerId)
        ? loansActiveOptions.map(loan =>
            loan.readerId === readerId ? { ...loan, option: item } : loan
          )
        : [...loansActiveOptions, { readerId: readerId, option: item }]
    )
  }

  return {
    showMoveUserModal,
    showUserModal,
    userModalType,
    userInfo,
    loansActiveOptions,
    handleResetUserPassword,
    handleShowUserCardID,
    handleOpenUserModal,
    handleCloseUserModal,
    handleOpenMoveUserModal,
    handleCloseMoveUserModal,
    handleClickOnStudentCardButton,
    setLoansActiveOptions,
    handleOnChangeLoansOptions,
    handleClickMakeLoan
  }
}

export { useUsersTable }
